import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import Quill from 'quill/quill';
import { v4 as uuid } from 'uuid';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.custom.css';
const style = StyleSheet.create({
    container: {
        height: 120,
    },
});
/* We keep them to preserve compatibility when removed */
const LEGACY_FONT_FAMILIES = ['Arial', 'Courier-New', 'Times-New-Roman'];
const FONT_FAMILIES = ['Lato', 'Oswald', 'Poppins', 'PT-Serif'];
/* We want to prevent the editor sizes from scaling up/down */
/* The editor sizes will be defined in 'px', while those in the presentation will be defined in 'vw' */
const FONT_SIZES_PX = ['12px', '16px', '20px', '24px', '28px'];
/* We want to allow the anchor sizes to scale up/down accordingly */
const FONT_SIZES_VW = ['1vw', '1.7vw', '2.4vw', '3.1vw', '3.8vw'];
/* In Editor Mode will always be in PX */
export var FONT_SIZE_MEASURE;
(function (FONT_SIZE_MEASURE) {
    FONT_SIZE_MEASURE["PX"] = "PX";
    FONT_SIZE_MEASURE["VW"] = "VW";
})(FONT_SIZE_MEASURE || (FONT_SIZE_MEASURE = {}));
function initQuillDependencies() {
    /* Register Specific Font Families */
    const Font = Quill.import('formats/font');
    Font.whitelist = [...FONT_FAMILIES, ...LEGACY_FONT_FAMILIES];
    Quill.register(Font, true);
    /* Register Specific Font Sizes */
    const Size = Quill.import('attributors/style/size');
    Size.whitelist = [...FONT_SIZES_PX, ...FONT_SIZES_VW];
    Quill.register(Size, true);
    /* Sets default Font Size */
    const Block = Quill.import('blots/block');
    class CustomBlock extends Block {
        static create(value) {
            const node = super.create(value);
            node.style.fontSize = FONT_SIZES_PX[2];
            node.style.fontFamily = FONT_FAMILIES[0];
            return node;
        }
    }
    CustomBlock.tagName = 'P';
    Quill.register(CustomBlock, true);
}
initQuillDependencies();
/* Changes the font sizes to the required measure */
function prepareRawDelta(delta, fontSizeMeasure) {
    const targetMeasure = fontSizeMeasure === FONT_SIZE_MEASURE.VW ? FONT_SIZES_VW : FONT_SIZES_PX;
    delta.ops.forEach((entry) => {
        const size = entry?.attributes?.size;
        if (typeof size === 'string') {
            /* First checks if the current value is in PX and needs to change to VW */
            let currentMeasureIndex = FONT_SIZES_PX.indexOf(size);
            if (currentMeasureIndex !== -1) {
                entry.attributes.size = targetMeasure[currentMeasureIndex];
            }
            /* Checks if the current value is in VW and needs toc hange to PX */
            currentMeasureIndex = FONT_SIZES_VW.indexOf(size);
            if (currentMeasureIndex !== -1) {
                entry.attributes.size = targetMeasure[currentMeasureIndex];
            }
            /* If no size is set, sets the default one */
        }
        else if (entry?.attributes) {
            entry.attributes.size = targetMeasure[2];
        }
        else {
            entry.attributes = {
                size: targetMeasure[2],
            };
        }
    });
    return delta;
}
/* Indicates TextEditor's modules */
const RICH_TEXT_EDITOR_MODULES = {
    toolbar: [
        [{ 'font': FONT_FAMILIES }, { size: FONT_SIZES_PX }],
        [{ 'color': [] }, 'bold', 'italic', 'underline', { 'align': [] }],
    ],
};
/* Indicates valid text's settings */
const RICH_TEXT_EDITOR_FORMATS = [
    'font', 'size', 'color', 'align',
    'bold', 'italic', 'underline',
];
/**
 * Rich Text Editor Component using ReactQuill.
 *
 * @param {Object} props - The props object.
 * @param {string} props.value - The initial value for the editor.
 * @param {string} props.fontSizeMeasure - The measure in which the text will be displayed.
 * @param {function(string): void} props.onChange - Callback function triggered when the text changes.
 * @param {boolean} props.isReadOnly - Optional. Whether the editor is in read-only mode.
 * @param {number} props.maxLength - Optional. Sets the maximum amount of characters the user can enter.
 * @returns {JSX.Element} The rendered ReactQuill editor component.
 * @inheritdoc https://quilljs.com/
 */
const RichText = (props) => {
    const { value = '', isReadOnly, maxLength, fontSizeMeasure = FONT_SIZE_MEASURE.PX } = props;
    const quillRef = useRef(null);
    const textEditorInstanceId = useMemo(() => 'rich-text-editor-' + uuid(), []);
    /* MEMOIZED FUNCTIONS */
    /* If the max length is reached, trims the text. */
    const verifyTextLengthAndTrimExcess = useCallback(() => {
        const textLength = quillRef.current?.getLength();
        const isLimitExceeded = !!maxLength && !!textLength &&
            textLength >= maxLength;
        if (isLimitExceeded) {
            const excessLength = textLength - maxLength;
            // [NOTE]: there is no simple way to set a maxLength, so we
            // will truncate the text that exceeds the limit.
            quillRef.current?.deleteText(maxLength, excessLength);
        }
    }, []);
    const onTextChange = useCallback((_, __, source) => {
        if (source === 'user') {
            verifyTextLengthAndTrimExcess();
        }
        props.onChange?.(JSON.stringify(quillRef.current?.getContents()));
    }, []);
    const updateEditorValue = useCallback((value) => {
        try {
            quillRef.current?.setContents(prepareRawDelta(JSON.parse(value), fontSizeMeasure));
        }
        catch (e) {
            quillRef.current?.setText(value || '');
        }
    }, []);
    /* Inits Editor */
    useEffect(() => {
        quillRef.current = new Quill(`#${textEditorInstanceId}`, {
            formats: RICH_TEXT_EDITOR_FORMATS,
            modules: RICH_TEXT_EDITOR_MODULES,
            theme: isReadOnly ? 'bubble' : 'snow',
            readOnly: isReadOnly,
        });
        if (!isReadOnly) {
            quillRef.current?.on('text-change', onTextChange);
            quillRef.current?.focus();
            const defaultSize = fontSizeMeasure === FONT_SIZE_MEASURE.VW ? FONT_SIZES_VW[2] : FONT_SIZES_PX[2];
            /* Sets the default fontSize upon going to a new line */
            /* [NOTE]: They purposely reset all styling upon going into a new line.
            *  We can patch the library to keep the previous style instead of their default behavior. */
            quillRef.current?.format('size', defaultSize);
            quillRef.current?.format('font', FONT_FAMILIES[0]);
            quillRef.current?.root.addEventListener('keydown', (event) => {
                if (event.key === 'Enter') {
                    quillRef.current?.format('size', defaultSize);
                    quillRef.current?.format('font', FONT_FAMILIES[0]);
                }
            });
        }
        /* Sets initial value */
        if (value) {
            updateEditorValue(value);
        }
        return () => {
            quillRef.current?.off('text-change', onTextChange);
        };
    }, [onTextChange, updateEditorValue]);
    /* Updates the content in ReadOnlyMode (when used from the player and edited in Beacon) */
    useEffect(() => {
        if (isReadOnly) {
            updateEditorValue(value);
        }
    }, [value]);
    return <div id={textEditorInstanceId} style={isReadOnly ? {} : style.container}/>;
};
RichText.Editor = (props) => <RichText {...props}/>;
RichText.Anchor = (props) => <RichText isReadOnly {...props}/>;
export default RichText;
//# sourceMappingURL=RichTextEditor.js.map